import { supabase } from '../lib/supabase'

/**
 * Result type for storage cleanup operations
 */
interface StorageCleanupResult {
    success: boolean
    deletedCount: number
    errors: string[]
}

/**
 * Result type for database cleanup operations
 */
interface DatabaseCleanupResult {
    success: boolean
    deletedTables: string[]
    errors: string[]
}

/**
 * Result type for complete cleanup operations
 */
interface CompleteCleanupResult {
    success: boolean
    storageDeleted: number
    tablesDeleted: string[]
    errors: string[]
}

/**
 * Delete all files from storage for a given agent
 * Recursively lists and deletes all files in the agent's directory
 */
export async function deleteAgentStorageFiles(agentId: string): Promise<StorageCleanupResult> {
    const errors: string[] = []
    let deletedCount = 0

    try {
        console.log(`[Cleanup] Starting storage cleanup for agent: ${agentId}`)

        // Recursively list all files in agent's directory
        const allFilePaths: string[] = []

        async function listRecursive(path: string): Promise<void> {
            const { data: items, error } = await supabase.storage
                .from('agent-files')
                .list(path)

            if (error) {
                // If folder doesn't exist, that's ok - agent might not have files
                if (error.message.includes('not found') || error.message.includes('does not exist')) {
                    console.log(`[Cleanup] No files found at path: ${path}`)
                    return
                }
                errors.push(`Failed to list ${path}: ${error.message}`)
                return
            }

            if (!items || items.length === 0) {
                return
            }

            for (const item of items) {
                const fullPath = path ? `${path}/${item.name}` : item.name

                // Check if it's a file (has metadata) or folder
                if (item.metadata) {
                    allFilePaths.push(fullPath)
                } else {
                    // It's a folder, recurse into it
                    await listRecursive(fullPath)
                }
            }
        }

        // Start recursive listing from agent's root directory
        await listRecursive(agentId)

        if (allFilePaths.length === 0) {
            console.log(`[Cleanup] No files to delete for agent: ${agentId}`)
            return { success: true, deletedCount: 0, errors: [] }
        }

        console.log(`[Cleanup] Found ${allFilePaths.length} files to delete`)

        // Delete files in batches of 100 (Supabase limit)
        const batchSize = 100
        for (let i = 0; i < allFilePaths.length; i += batchSize) {
            const batch = allFilePaths.slice(i, i + batchSize)

            console.log(`[Cleanup] Deleting batch ${Math.floor(i / batchSize) + 1}/${Math.ceil(allFilePaths.length / batchSize)}`)

            const { error: deleteError } = await supabase.storage
                .from('agent-files')
                .remove(batch)

            if (deleteError) {
                errors.push(`Failed to delete batch: ${deleteError.message}`)
                console.error(`[Cleanup] Error deleting batch:`, deleteError)
            } else {
                deletedCount += batch.length
            }
        }

        console.log(`[Cleanup] Storage cleanup complete. Deleted ${deletedCount} files`)

        return {
            success: errors.length === 0,
            deletedCount,
            errors
        }
    } catch (error) {
        const errorMsg = `Unexpected error during storage cleanup: ${error}`
        errors.push(errorMsg)
        console.error(`[Cleanup]`, error)
        return { success: false, deletedCount, errors }
    }
}

/**
 * Delete all database records related to an agent
 * Attempts to clean up agent_files and agent_file_versions tables
 */
export async function deleteAgentDatabaseRecords(agentId: string): Promise<DatabaseCleanupResult> {
    const errors: string[] = []
    const deletedTables: string[] = []

    try {
        console.log(`[Cleanup] Starting database cleanup for agent: ${agentId}`)

        // Try to delete from agent_file_versions (if table exists)
        try {
            const { error: versionsError, count } = await supabase
                .from('agent_file_versions')
                .delete({ count: 'exact' })
                .eq('agent_id', agentId)

            if (versionsError) {
                // Table might not exist - that's ok
                if (!versionsError.message.includes('does not exist')) {
                    errors.push(`Failed to delete file versions: ${versionsError.message}`)
                }
            } else {
                deletedTables.push('agent_file_versions')
                console.log(`[Cleanup] Deleted ${count || 0} records from agent_file_versions`)
            }
        } catch {
            // Table doesn't exist - ignore
            console.log(`[Cleanup] agent_file_versions table not found (ok)`)
        }

        // Try to delete from agent_files (if table exists)
        try {
            const { error: filesError, count } = await supabase
                .from('agent_files')
                .delete({ count: 'exact' })
                .eq('agent_id', agentId)

            if (filesError) {
                // Table might not exist - that's ok
                if (!filesError.message.includes('does not exist')) {
                    errors.push(`Failed to delete agent files: ${filesError.message}`)
                }
            } else {
                deletedTables.push('agent_files')
                console.log(`[Cleanup] Deleted ${count || 0} records from agent_files`)
            }
        } catch {
            // Table doesn't exist - ignore
            console.log(`[Cleanup] agent_files table not found (ok)`)
        }

        // Note: conversations and messages should be handled by CASCADE DELETE
        // if foreign keys are properly configured in the database

        console.log(`[Cleanup] Database cleanup complete. Cleaned tables: ${deletedTables.join(', ') || 'none'}`)

        return {
            success: errors.length === 0,
            deletedTables,
            errors
        }
    } catch (error) {
        const errorMsg = `Unexpected error during database cleanup: ${error}`
        errors.push(errorMsg)
        console.error(`[Cleanup]`, error)
        return { success: false, deletedTables, errors }
    }
}

/**
 * Complete cleanup of agent data (storage + database)
 * This is the main function to call when deleting an agent
 */
export async function cleanupAgentData(agentId: string): Promise<CompleteCleanupResult> {
    console.log(`[Cleanup] Starting complete cleanup for agent: ${agentId}`)
    const allErrors: string[] = []

    // Step 1: Clean storage files
    const storageResult = await deleteAgentStorageFiles(agentId)
    if (!storageResult.success) {
        allErrors.push(...storageResult.errors)
        console.warn(`[Cleanup] Storage cleanup had errors:`, storageResult.errors)
    }

    // Step 2: Clean database records
    const dbResult = await deleteAgentDatabaseRecords(agentId)
    if (!dbResult.success) {
        allErrors.push(...dbResult.errors)
        console.warn(`[Cleanup] Database cleanup had errors:`, dbResult.errors)
    }

    const result = {
        success: allErrors.length === 0,
        storageDeleted: storageResult.deletedCount,
        tablesDeleted: dbResult.deletedTables,
        errors: allErrors
    }

    console.log(`[Cleanup] Complete cleanup finished:`, result)

    return result
}
